import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './baseQueryWithAuth';

export interface AdminStats {
  users: { total: number; pro: number; free: number };
  repos: { total: number };
  scans: { total: number; loc_scanned: number };
  issues: {
    total: number;
    by_severity: Record<string, number>;
    by_category: Record<string, number>;
  };
  fixes: {
    merged: number;
    critical_eliminated: number;
    high_eliminated: number;
    security_eliminated: number;
    prs_raised: number;
    dev_hours_saved: number;
    dev_cost_saved: number;
    security_risk_eliminated: number;
    total_value_delivered: number;
  };
  refactor: { prs_raised: number; prs_merged: number };
  carbon: { co2g_saved_from_merged_fixes: number; kwh_saved_from_merged_fixes: number };
}

export interface LanguageAnalysis {
  issues_by_language: Record<string, Record<string, number>>;
  category_mix_by_language: Record<string, Record<string, number>>;
  coverage_gaps: { language: string; category: string }[];
  tool_activity_trend: Record<string, Record<string, number>>;
  density_per_1000_loc: Record<string, number>;
  trend_weeks: number;
}

export interface LanguageAnalysisInsights {
  insights: string;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getAdminStats: builder.query<AdminStats, void>({
      query: () => '/admin/stats',
    }),
    getLanguageAnalysis: builder.query<LanguageAnalysis, void>({
      query: () => '/admin/analysis/languages',
    }),
    // Deliberately separate from getLanguageAnalysis (its own slower query,
    // not fetched together) — the numbers should render instantly while the
    // AI narrative loads progressively, since the LLM call takes noticeably
    // longer than the deterministic aggregates.
    getLanguageAnalysisInsights: builder.query<LanguageAnalysisInsights, void>({
      query: () => '/admin/analysis/languages/insights',
    }),
  }),
});

export const {
  useGetAdminStatsQuery,
  useGetLanguageAnalysisQuery,
  useGetLanguageAnalysisInsightsQuery,
} = adminApi;
